import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CheckCheck, FileText, AlertCircle, Loader2, Undo } from 'lucide-react'; 
import { Message } from '../../types/chat';
import { getMessageMediaLink } from '../../services/whatsappService'; // Import the new service

interface MessageBubbleProps {
  message: Message;
  onQuoteMessage?: (messageId: string) => void;
}

const mediaMessageTypes = ['image', 'audio', 'video', 'document', 'file', 'ptt', "sticker"];

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onQuoteMessage }) => {
  const { t } = useTranslation();
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isLoadingMedia, setIsLoadingMedia] = useState<boolean>(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [actualFileName, setActualFileName] = useState<string | null>(null);
  
  // Check if message is deleted
  const isDeleted = message.deleted === true;

  const VIEW_MEDIA = `${import.meta.env.VITE_API_BASE_URL}/whatsapp/media/view/${message.id}`;
  
  const isPotentialMediaMessage = mediaMessageTypes.includes(message.messageType);

  useEffect(() => {
    const fetchMediaUrl = async () => {
      // Reset states for new message or if message content changes that might affect media
      setMediaUrl(null);
      setActualFileName(null);
      setMediaError(null);

      if (message.id && isPotentialMediaMessage) {
        setIsLoadingMedia(true);
        try {
          const data = await getMessageMediaLink(message.id);
          
          if (data && data.url) {
            setMediaUrl(data.url);
            setActualFileName(data.fileName || message.content || 'Download');
          } else {
            // This case might occur if the API returns a 2xx response but no URL (unlikely with current setup)
            throw new Error('Media URL not found in response.');
          }
        } catch (error) {
          console.error("Failed to fetch media URL for message ID " + message.id + ":", error);
          // Un 404 significa que el mensaje nunca tuvo un adjunto guardado
          // (ej. la subida a S3 falló en su momento) — no es un error real
          // que mostrarle al usuario, es el mismo estado que "sin media".
          // Dejar mediaUrl/mediaError en null cae en el bloque
          // "mediaContentUnavailable" de más abajo, en vez de un banner rojo
          // con el código HTTP crudo.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== 404) {
            setMediaError(error instanceof Error ? error.message : 'An unknown error occurred.');
          }
        } finally {
          setIsLoadingMedia(false);
        }
      } else {
        // If it's not a potential media message or has no ID, ensure loading is false.
        setIsLoadingMedia(false);
      }
    };

    fetchMediaUrl();
  }, [message.id, message.messageType, message.content, isPotentialMediaMessage]); // Added isPotentialMediaMessage to deps


  const renderMedia = () => {
    if (!mediaUrl) return null;
    // Map 'ptt' to 'audio' for rendering if necessary, or handle 'ptt' directly if your audio player supports it
    const displayType = message.messageType === 'ptt' ? 'audio' : message.messageType;

    switch (displayType) {
      case 'image':
        return (
          <>
            <a
              href={VIEW_MEDIA}
              download={actualFileName ||message.content || t('imageContent')}
              rel="noopener noreferrer"
            >
            <img
              src={VIEW_MEDIA}
              alt={message.content || t('imageContent')}
              className="max-w-full h-auto rounded-md my-1"
              style={{ maxHeight: '300px' }}
            />
            </a>
          
          </>
        );
      case 'sticker':
        return (
          <>

            <img
              src={VIEW_MEDIA}
              alt={message.content || t('imageContent')}
              className="max-w-full h-auto rounded-md my-1"          
              style={{
                maxHeight: '180px'
              }}
            />
          
          </>
        );
      case 'audio':
        return (
          <audio controls src={VIEW_MEDIA} className="my-1">
            {t('yourBrowserDoesNotSupportAudio')}
          </audio>
        );
      case 'video':
        return (
          <video controls src={VIEW_MEDIA} className="max-w-full h-auto rounded-md my-1" style={{ maxHeight: '300px' }}>
            {t('yourBrowserDoesNotSupportVideo')}
          </video>
        );
      case 'document':
      case 'file':
      default:

        return (
          <a
            href={VIEW_MEDIA}
            download={actualFileName || message.content || t('downloadFile')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-600 hover:text-blue-800 visited:text-purple-600 my-1 p-2 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            <FileText size={20} className="mr-2 flex-shrink-0" />
            <span className="truncate">{actualFileName || message.content || t('downloadFile')}</span>
          </a>
        );
    }
  };

  // This variable now determines if the media block (loading, error, or content) should be shown
  const shouldDisplayMediaBlock = isPotentialMediaMessage;

  // Si el mensaje está eliminado, mostrar solo el texto de eliminado
  if (isDeleted) {
    return (
      <div className={`flex mb-4 ${message.isSent ? 'justify-end' : 'justify-start'}`}>
        <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-lg bg-gray-200 border border-gray-300">
          <div className="flex items-center text-sm text-gray-600 italic">
            <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            <span>{message.isSent ? t('youDeletedThisMessage') : t('thisMessageWasDeleted')}</span>
          </div>
          <div className="flex items-center justify-end mt-1 text-xs text-gray-500">
            <span className="mr-1">{message.timestamp}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex mb-4 ${message.isSent ? 'justify-end' : 'justify-start'} group relative`}>
      <div
        className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
          message.messageType !== 'sticker' ? message.isSent 
            ? 'bg-green-500 text-white shadow-sm'
            : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
          : 'bg-transparent'

        }`}
      >
        {/* Media rendering section */}
        {shouldDisplayMediaBlock && (
          <>
            {isLoadingMedia && (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="animate-spin h-6 w-6 text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">{t('loadingMedia')}</span>
              </div>
            )}

            {mediaError && !isLoadingMedia && (
              <div className="flex items-center p-2 text-red-600 bg-red-100 rounded-md">
                <AlertCircle size={20} className="mr-2 flex-shrink-0" />
                <span className="text-sm">{t('errorMedia', { error: mediaError })}</span>
              </div>
            )}

            {!isLoadingMedia && !mediaError && mediaUrl && renderMedia()}
            
            {!isLoadingMedia && !mediaError && !mediaUrl && (
              <div className="flex items-center p-2 text-yellow-600 bg-yellow-100 rounded-md">
                <AlertCircle size={20} className="mr-2 flex-shrink-0" />
                <span className="text-sm">{t('mediaContentUnavailable')}</span>
              </div>
            )}
          </>
        )}

        {/* Text content (caption or text message) */}
        {(message.messageType === 'text' || (shouldDisplayMediaBlock && message.content)) && (
          <div className={`text-sm leading-relaxed ${shouldDisplayMediaBlock && mediaUrl ? 'mt-1' : ''}`}>
            {/* {formatMessageContent(message.content)} */}
            {message.content}
          </div>
        )}

        {/* Timestamp and read status */}
        <div className={`flex items-center justify-end mt-1 text-xs ${
          message.isSent ? (shouldDisplayMediaBlock && mediaUrl ? 'text-green-50':'text-green-100') : 'text-gray-500'
        }`}>
          <span className="mr-1">{message.timestamp}</span>
          {message.isSent && (
            <div className="ml-1">
              {message.isRead ? (
                <CheckCheck className="w-4 h-4 text-blue-200" />
              ) : (
                <Check className="w-4 h-4" />
              )}
            </div>
          )}
        </div>

        {onQuoteMessage && (
            <button
              onClick={() => onQuoteMessage(message.id)}
              className={`absolute p-1 bg-gray-100 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity ${message.isSent ? '-top-2 -right-2' : '-top-2 -left-2'}`}
              title={t('quoteThisMessage')}
              style={{ lineHeight: '1' }}
            >
              <Undo className="w-5 h-5"/>
            </button>
          )}
      </div>
    </div>
  );
};

export default MessageBubble;